import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Search, Home, DollarSign, MessageCircle, ArrowRight, Bed, Bath } from "lucide-react";
import logoImg from "@/assets/logo.png";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import WhatsIncluded from "@/components/WhatsIncluded";
import BuyerAgentEducation from "@/components/BuyerAgentEducation";
import FounderNote from "@/components/FounderNote";
import HomeFAQ from "@/components/HomeFAQ";

const MobileHome = () => {
  const { user, profile } = useAuth();
  const [featured, setFeatured] = useState<any[]>([]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("listings")
        .select("id,address,city,state,price,bedrooms,bathrooms,photo_url,square_feet")
        .eq("status", "approved")
        .gt("price", 0)
        .order("created_at", { ascending: false })
        .limit(6);
      setFeatured(data || []);
    })();
  }, []);

  const quickActions = [
    { to: "/listings", icon: Search, label: "Search homes", subtitle: "Browse Central FL", color: "bg-gold" },
    { to: "/list-property", icon: Home, label: "Sell your home", subtitle: "Flat 0.50% fee", color: "bg-wood/40" },
    { to: "/", icon: DollarSign, label: "Estimate value", subtitle: "Free instant report", color: "bg-linen", scrollTo: "estimator" },
    { to: "/property-chat", icon: MessageCircle, label: "Ask Concierge", subtitle: "24/7 guidance", color: "bg-canvas-deep" },
  ];

  return (
    <div
      className="md:hidden min-h-screen bg-canvas"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      {/* Hero strip */}
      <div className="px-5 pt-20 pb-5">
        <img src={logoImg} alt="REALV3" className="h-14 w-auto mb-6" />
        <h1 className="font-display text-3xl font-semibold text-ink leading-[1.1] mb-2">
          Hi{profile?.full_name ? `, ${profile.full_name.split(" ")[0]}` : ""}.
          <br />
          <span className="text-gold-deep accent-italic">Keep what's yours.</span>
        </h1>
        <p className="text-sm text-ink-soft leading-relaxed">
          List, tour, and close in Central Florida — without a 3-6% commission.
        </p>
      </div>

      {/* Search shortcut */}
      <div className="px-5 mb-6">
        <Link
          to="/listings"
          className="flex items-center gap-3 bg-card rounded-2xl shadow-soft border border-ink/5 px-4 py-4"
        >
          <Search className="w-5 h-5 text-ink-soft" />
          <span className="text-sm text-ink-soft flex-1">Search city, ZIP, or address</span>
        </Link>
      </div>

      {/* Quick actions grid */}
      <div className="px-5 mb-8">
        <div className="grid grid-cols-2 gap-3">
          {quickActions.map((a) => {
            const Icon = a.icon;
            return (
              <Link
                key={a.label}
                to={a.to}
                className="bg-card rounded-2xl p-4 shadow-soft border border-ink/5 active:scale-[0.98] transition-transform"
              >
                <div className={`w-10 h-10 rounded-full ${a.color} flex items-center justify-center mb-3`}>
                  <Icon className="w-5 h-5 text-ink" strokeWidth={1.75} />
                </div>
                <div className="font-display text-sm font-semibold text-ink leading-tight">{a.label}</div>
                <div className="text-[11px] text-ink-soft mt-0.5">{a.subtitle}</div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Featured listings */}
      <div className="mb-8">
        <div className="flex items-center justify-between px-5 mb-3">
          <h2 className="font-display text-lg font-semibold text-ink">Fresh listings</h2>
          <Link to="/listings" className="text-xs font-medium text-gold-deep flex items-center gap-1">
            See all <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2 px-5 snap-x snap-mandatory scrollbar-none">
          {featured.map((l) => (
            <Link
              key={l.id}
              to={`/listings/${l.id}`}
              className="flex-shrink-0 w-64 snap-start bg-card rounded-2xl overflow-hidden shadow-soft border border-ink/5"
            >
              <div className="aspect-[16/10] bg-muted relative">
                {l.photo_url ? (
                  <img src={l.photo_url} alt={l.address} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-ink-soft text-xs">No photo</div>
                )}
              </div>
              <div className="p-3">
                <div className="font-display font-bold text-ink tabular-nums">${l.price?.toLocaleString()}</div>
                <div className="flex gap-2 text-[11px] text-ink-soft mt-1">
                  <span className="flex items-center gap-0.5"><Bed className="w-3 h-3" />{l.bedrooms || 0}</span>
                  <span className="flex items-center gap-0.5"><Bath className="w-3 h-3" />{l.bathrooms || 0}</span>
                  <span>{(l.square_feet || 0).toLocaleString()} sqft</span>
                </div>
                <div className="text-[11px] text-ink-soft truncate mt-1">
                  {l.city}{l.state && `, ${l.state}`}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Sell CTA */}
      <div className="px-5 mb-8">
        <Link
          to="/list-property"
          className="block bg-ink text-canvas rounded-2xl p-6 shadow-lift relative overflow-hidden"
        >
          <div className="relative z-10">
            <span className="text-[11px] uppercase tracking-[0.2em] text-gold">For sellers</span>
            <h3 className="font-display text-2xl font-semibold mt-2 leading-tight">
              Save $22k+ on your<br />next home sale.
            </h3>
            <p className="text-xs text-canvas/70 mt-2 mb-4">Flat 0.50% fee. No agent. No surprises.</p>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gold">
              List your home <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Link>
      </div>

      {!user && (
        <div className="px-5 mb-4">
          <Link
            to="/auth"
            className="block text-center bg-gold text-ink font-medium py-3.5 rounded-xl shadow-soft"
          >
            Create your free account
          </Link>
        </div>
      )}

      {/* Trust + education sections (mobile) */}
      <WhatsIncluded />
      <BuyerAgentEducation />
      <FounderNote />
      <HomeFAQ />

      <div style={{ height: "calc(env(safe-area-inset-bottom) + 160px)" }} />
    </div>
  );
};

export default MobileHome;
